'use client';

/* @jsxRuntime classic */
// The Roma Vitest/React 17 setup resolves JSX's jsxDEV helper incorrectly for this package.

import React, { createContext, useContext } from 'react';
import useSheetContextState, { type SheetContextState } from './hooks/useSheetContextState';
import type { SheetBehaviourProps } from './sheetTypes';

const SheetContext = createContext<SheetContextState>({
    sheetContainerRef: { current: null },
    sheetContentRef: { current: null },
    sheetRef: { current: null },
    snapPointsState: [],
    topInset: 0,
    isSheetMounted: false,
    setIsSheetMounted: () => {},
    requestClose: () => {},
    handleSheetTouchStart: () => {},
    handleSheetTouchMove: () => {},
    handleSheetTouchEnd: () => {},
    handleSheetTouchCancel: () => {},
});

export const useSheetContext = () => useContext(SheetContext);

/** The snap points the sheet can currently settle on, including any measured content-height point. */
export const useSheetSnapPoints = (): number[] => useSheetContext().snapPointsState;

/** Asks the enclosing sheet to close, for content that needs to dismiss the sheet itself. */
export const useSheetClose = (): (() => void) => useSheetContext().requestClose;

type SheetContextProviderProps = SheetBehaviourProps & {
    children: (isSheetMounted: boolean) => React.ReactNode;
};

export const SheetContextProvider = ({ children, ...props }: SheetContextProviderProps) => {
    const sheetContext = useSheetContextState(props);

    return <SheetContext.Provider value={sheetContext}>{children(sheetContext.isSheetMounted)}</SheetContext.Provider>;
};
